import React from 'react';
import { Form, FormikProps } from 'formik';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { DiagramUploadRow } from '../diagram-upload-row.component';
import { FormatTemplateMetadataFields } from '../format-template-metadata-fields.component';
import { messages } from '../format-template.messages';
import { ISaveFormatTemplateFields } from './save-format-template-modal.types';

const FORM_ID = 'save-format-template-form';

interface ISaveFormatTemplateModalProps {
  open: boolean;
  onClose: () => void;
  stageCount: number;
  bucketOptions: string[];
  form: FormikProps<ISaveFormatTemplateFields>;
  pendingDiagramUrl?: string;
  onSelectDiagram: (file: File) => void;
}

export const SaveFormatTemplateModal: React.FC<ISaveFormatTemplateModalProps> = ({
  open,
  onClose,
  stageCount,
  bucketOptions,
  form,
  pendingDiagramUrl,
  onSelectDiagram,
}) => {
  const { status, isSubmitting } = form;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Form lives inside the Dialog so the portal-rendered fields share its id for focus targeting. */}
      <Form id={FORM_ID}>
        <FocusFirstInvalidField formId={FORM_ID} />
        <DialogTitle><IntlMsg {...messages.saveTitle} /></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            <IntlMsg {...messages.savingStages} values={{ count: stageCount, stageWord: pluralize(stageCount, 'stage') }} />
          </Typography>
          <FormatTemplateMetadataFields form={form} bucketOptions={bucketOptions} />
          <Box mt={3}>
            <Typography variant="subtitle2"><IntlMsg {...messages.diagramTitle} /></Typography>
            <DiagramUploadRow
              alt="Template diagram"
              pendingPreviewUrl={pendingDiagramUrl}
              pendingMessage={messages.diagramPendingCreate}
              uploading={false}
              disabled={isSubmitting}
              onSelectFile={onSelectDiagram}
            />
          </Box>
          {status && (
            <Box mt={1}>
              <FormHelperText error>{status}</FormHelperText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}><IntlMsg {...messages.cancel} /></Button>
          <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress size={18} style={{ marginRight: 8 }} />
                <IntlMsg {...messages.saving} />
              </>
            ) : <IntlMsg {...messages.saveTemplate} />}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
